import React from 'react';
import {
  LineChart, BarList, StatTile, HIERARCHY_LABELS, eventLabel,
  formatNumber, formatDateTime, formatMinutes,
} from './analyticsUtils';

const ACTIVITY_SERIES = [
  { key: 'events', label: 'Events', color: '#3b82f6' },
  { key: 'sessions', label: 'Sessions', color: '#a3e635' },
];

const MentorDrilldown = ({ data, loading, onBack }) => {
  if (loading && !data) {
    return (
      <div className="analytics-drilldown-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading</span>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const {
    mentor, summary, daily, features, screens,
    neverVisitedScreens, exports, appHealth,
  } = data;

  const screenItems = screens.map((s) => ({ label: s.screen, count: s.count }));
  const exportItems = exports.map((e) => ({
    label: `${e.report || 'unknown'} (${e.format || '--'})`,
    count: e.count,
  }));

  return (
    <div className="analytics-drilldown">
      <div className="analytics-drilldown-header">
        <button type="button" className="analytics-back-btn" onClick={onBack}>
          <i className="bi bi-arrow-left" aria-hidden="true" /> All mentors
        </button>
        <div className="analytics-drilldown-identity">
          <h2 className="analytics-drilldown-name">{mentor.name}</h2>
          <div className="analytics-drilldown-meta">
            <span className="analytics-role-badge">
              {HIERARCHY_LABELS[mentor.hierarchy] || mentor.hierarchy}
            </span>
            {mentor.email && <span className="analytics-muted">{mentor.email}</span>}
            {mentor.supervisor && (
              <span className="analytics-muted">
                Reports to {mentor.supervisor.name}
              </span>
            )}
            <span className="analytics-muted">
              {formatNumber(mentor.studentsCount)} students
            </span>
          </div>
        </div>
      </div>

      {summary.neverActive && (
        <div className="analytics-alert">
          <i className="bi bi-exclamation-triangle" aria-hidden="true" />
          {' '}This mentor recorded no app activity in the selected range.
        </div>
      )}

      <div className="analytics-stat-row">
        <StatTile label="Events" value={formatNumber(summary.events)} />
        <StatTile label="Sessions" value={formatNumber(summary.sessions)} />
        <StatTile
          label="Active days"
          value={`${summary.activeDays} / ${summary.rangeDays}`}
          sub="Days with any activity"
          tone={summary.activeDays === 0 ? 'warn' : undefined}
        />
        <StatTile label="Time in app" value={`${formatNumber(summary.foregroundHours)}h`} />
        <StatTile
          label="Avg session"
          value={formatMinutes(summary.avgSessionMinutes)}
          sub={summary.timedSessions
            ? `From ${formatNumber(summary.timedSessions)} timed sessions`
            : 'No timing data'}
        />
        <StatTile
          label="Last seen"
          value={summary.lastSeenAt ? formatDateTime(summary.lastSeenAt) : 'Never'}
          tone={summary.neverActive ? 'warn' : undefined}
        />
      </div>

      <section className="analytics-card">
        <h3 className="analytics-card-title">Activity trend</h3>
        <LineChart data={daily} series={ACTIVITY_SERIES} />
      </section>

      <section className="analytics-card">
        <h3 className="analytics-card-title">Feature adoption</h3>
        <p className="analytics-card-note">
          Compared against the average per active mentor over the same range.
        </p>
        <div className="analytics-feature-grid">
          {features.map((f) => (
            <div key={f.name} className="analytics-feature-row">
              <span className="analytics-feature-name">{eventLabel(f.name)}</span>
              <span className="analytics-feature-count">{formatNumber(f.count)}</span>
              <span className="analytics-feature-peer">peer avg {f.peerAvg}</span>
              <span className={`analytics-feature-delta${deltaTone(f.deltaPct)}`}>
                {formatDelta(f.deltaPct)}
              </span>
            </div>
          ))}
        </div>
      </section>

      <div className="analytics-grid-2">
        <section className="analytics-card">
          <h3 className="analytics-card-title">Screens used</h3>
          <BarList items={screenItems} emptyText="No screen views in this range" />
        </section>

        <section className="analytics-card">
          <h3 className="analytics-card-title">
            Never opened
            <span className="analytics-table-count">{neverVisitedScreens.length}</span>
          </h3>
          <p className="analytics-card-note">
            Screens other mentors use that this mentor has not opened in this range.
          </p>
          {neverVisitedScreens.length === 0 ? (
            <div className="analytics-empty-chart">Has visited every screen peers use.</div>
          ) : (
            <ul className="analytics-never-list">
              {neverVisitedScreens.map((s) => (
                <li key={s.screen}>
                  <span>{s.screen}</span>
                  <span className="analytics-muted">{s.peerMentors} peers use it</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="analytics-card">
          <h3 className="analytics-card-title">Exports</h3>
          <BarList items={exportItems} emptyText="No exports in this range" />
        </section>

        <section className="analytics-card">
          <h3 className="analytics-card-title">App health</h3>
          <dl className="analytics-health-list">
            <div>
              <dt>Current version</dt>
              <dd>
                {appHealth.appVersion || '--'}
                {appHealth.isOutdated && (
                  <span className="analytics-outdated-badge">Outdated</span>
                )}
              </dd>
            </div>
            <div>
              <dt>Platform</dt>
              <dd>{appHealth.platform || '--'}</dd>
            </div>
            <div>
              <dt>Latest build seen</dt>
              <dd>{appHealth.latestOrgVersion || '--'}</dd>
            </div>
            <div>
              <dt>First seen</dt>
              <dd>{summary.firstSeenAt ? formatDateTime(summary.firstSeenAt) : '--'}</dd>
            </div>
          </dl>
        </section>
      </div>
    </div>
  );
};

/** `null` delta means peers do not use the feature either — no comparison to draw. */
const formatDelta = (pct) => {
  if (pct === null || pct === undefined) return 'no peer usage';
  if (pct > 0) return `+${pct}%`;
  return `${pct}%`;
};

const deltaTone = (pct) => {
  if (pct === null || pct === undefined) return ' neutral';
  if (pct > 0) return ' positive';
  if (pct < 0) return ' negative';
  return ' neutral';
};

export default MentorDrilldown;
